// Routing via Valhalla (primary) with OSRM as fallback.
// Both are free, no API key required.

import { withTimeout, hedgedRace } from './fetchUtils';
import { rlog, rwarn, rnow } from './routingLog';

const VALHALLA_HOSTS = [
  'https://valhalla1.openstreetmap.de',
  'https://valhalla2.openstreetmap.de',
];

const OSRM_BASE = 'https://router.project-osrm.org';

// Geoapify proxies (Cloud Functions) — motorcycle profile, hosted & reliable.
const GEOAPIFY_ROUTE_URL = 'https://us-central1-roamr-4f8ad.cloudfunctions.net/getGeoapifyRoute';
const GEOAPIFY_ISOLINE_URL = 'https://us-central1-roamr-4f8ad.cloudfunctions.net/getGeoapifyIsoline';
const GEOAPIFY_MATRIX_URL = 'https://us-central1-roamr-4f8ad.cloudfunctions.net/getGeoapifyMatrix';

// Reachable-area boundary (Pro). Returns [[lng,lat],...] or null on failure.
export async function fetchIsoline(lat, lng, rangeM, mode = 'drive', signal) {
  try {
    const res = await fetch(GEOAPIFY_ISOLINE_URL, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lat, lng, range: rangeM, type: 'distance', mode }), signal,
    });
    if (!res.ok) return null;
    const d = await res.json().catch(() => null);
    return Array.isArray(d?.boundary) && d.boundary.length ? d.boundary : null;
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    return null;
  }
}

// One source → many targets (Pro). Returns [{distance,time}|null] or null.
export async function fetchMatrix(sources, targets, mode = 'motorcycle', signal) {
  try {
    const res = await fetch(GEOAPIFY_MATRIX_URL, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sources, targets, mode }), signal,
    });
    if (!res.ok) return null;
    const d = await res.json().catch(() => null);
    return Array.isArray(d?.cells) ? d.cells : null;
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    return null;
  }
}

const VALHALLA_TIMEOUT_MS = 12_000;
const OSRM_TIMEOUT_MS = 8_000;
const GEOAPIFY_TIMEOUT_MS = 12_000;
// If Valhalla hasn't answered within this long, start OSRM too and take
// whichever finishes first — bounds worst-case latency (previously the full
// VALHALLA_TIMEOUT_MS on every drag when the public Valhalla mirrors are
// unhealthy) without giving up Valhalla's better route quality (it supports
// avoid-options; OSRM doesn't) whenever Valhalla is actually healthy.
const VALHALLA_HEDGE_MS = 3_500;

const COSTING = {
  driving: 'auto',
  cycling: 'bicycle',
  foot:    'pedestrian',
};

const OSRM_PROFILE = {
  driving: 'driving',
  cycling: 'cycling',
  foot:    'foot',
};

export async function fetchRoute(waypoints, profile = 'driving', avoidOptions = {}, signal, pro = false) {
  const valid = waypoints.filter(wp => wp.lat != null && wp.lng != null);
  if (valid.length < 2) return null;

  const t0 = rnow();
  rlog(`fetchRoute: ${valid.length} points, profile=${profile}, pro=${pro}`, avoidOptions);

  // Pro only: Geoapify (motorcycle profile) via our cached proxy. Returns null
  // on any failure so we transparently fall back. Free users skip straight to
  // the free engines (Valhalla / OSRM) below.
  if (pro) {
    const tg = rnow();
    try {
      rlog('→ trying Geoapify (Pro)…');
      const g = await fetchGeoapify(valid, avoidOptions, withTimeout(GEOAPIFY_TIMEOUT_MS, signal));
      if (g) {
        rlog(`✓ Geoapify won in ${Math.round(rnow() - tg)}ms — total ${Math.round(rnow() - t0)}ms`);
        return g;
      }
      rwarn(`✗ Geoapify unavailable/failed after ${Math.round(rnow() - tg)}ms — falling back to Valhalla`);
    } catch (err) {
      if (err.name === 'AbortError' && signal?.aborted) { rlog('aborted (superseded by a newer edit)'); throw err; }
      rwarn(`✗ Geoapify threw after ${Math.round(rnow() - tg)}ms:`, err.message);
    }
  }

  // Valhalla (both mirrors raced) is primary — better quality, honours
  // avoid-options. If it hasn't answered within VALHALLA_HEDGE_MS, ALSO start
  // OSRM and take whichever finishes first, so a slow/unhealthy Valhalla
  // mirror can no longer stall the whole request for the full timeout.
  const tv = rnow();
  rlog(`→ trying Valhalla (racing ${VALHALLA_HOSTS.length} mirrors), hedging with OSRM after ${VALHALLA_HEDGE_MS}ms…`);
  try {
    const { from, value } = await hedgedRace(
      () => Promise.any(
        VALHALLA_HOSTS.map(host =>
          fetchValhalla(host, valid, profile, avoidOptions, withTimeout(VALHALLA_TIMEOUT_MS, signal))
        )
      ),
      () => fetchOSRM(valid, profile, withTimeout(OSRM_TIMEOUT_MS, signal)),
      VALHALLA_HEDGE_MS,
      () => rwarn(`Valhalla still hasn't answered after ${VALHALLA_HEDGE_MS}ms — starting OSRM as a hedge (won't honour avoid-options if it wins)`),
    );
    if (from === 'primary') {
      rlog(`✓ Valhalla won in ${Math.round(rnow() - tv)}ms — total ${Math.round(rnow() - t0)}ms`);
    } else {
      rlog(`✓ OSRM (hedge) won in ${Math.round(rnow() - tv)}ms — total ${Math.round(rnow() - t0)}ms (no per-leg data — next single-waypoint edit will do a FULL reroute, not a fast partial one)`);
    }
    return value;
  } catch (err) {
    if (signal?.aborted) { rlog('aborted (superseded by a newer edit)'); throw new DOMException('Aborted', 'AbortError'); }
    rwarn(`✗ Both Valhalla and the OSRM hedge failed after ${Math.round(rnow() - tv)}ms — no engine could route this`);
    throw err instanceof Error ? err : new Error('No routing engine could answer.');
  }
}

// Re-routes only the legs marked null in `legs` (the ones touching a waypoint
// that just moved), reusing every other leg's already-computed path untouched,
// then recombines everything into one geometry + summed totals — so dragging
// one waypoint mid-route doesn't reroute the whole thing.
export async function fetchStaleLegs(waypoints, legs, profile, avoidOptions, signal, pro) {
  const staleCount = legs.filter(l => l === null).length;
  const t0 = rnow();
  rlog(`fetchStaleLegs: ${staleCount} of ${legs.length} legs stale — re-routing only those, reusing the rest`);
  const refetched = await Promise.all(legs.map((leg, i) => {
    if (leg !== null) return leg;
    const a = waypoints[i], b = waypoints[i + 1];
    if (a?.lat == null || b?.lat == null) return null; // shouldn't happen — caller checked alignment
    return fetchRoute([a, b], profile, avoidOptions, signal, pro);
  }));
  if (refetched.some(leg => leg == null)) {
    rwarn(`fetchStaleLegs: a stale leg failed to resolve after ${Math.round(rnow() - t0)}ms — falling back to a full recompute`);
    return null; // let the caller fall back to a full recompute
  }
  rlog(`fetchStaleLegs: done in ${Math.round(rnow() - t0)}ms`);

  const coords = [];
  let distance = 0, duration = 0;
  for (const leg of refetched) {
    const legCoords = leg.geometry.coordinates;
    coords.push(...(coords.length ? legCoords.slice(1) : legCoords));
    distance += leg.distance;
    duration += leg.duration;
  }

  return {
    geometry: { type: 'LineString', coordinates: coords },
    distance: Math.round(distance),
    duration: Math.round(duration),
    // Strip each leg's own nested `legs` (single-pair fetches never have one) so
    // the stored array shape stays uniform: one flat {geometry,distance,duration} per leg.
    legs: refetched.map(({ geometry, distance: d, duration: t }) => ({ geometry, distance: d, duration: t })),
  };
}

async function fetchGeoapify(valid, avoidOptions, signal) {
  const points = valid.map(wp => [wp.lng, wp.lat]); // proxy expects [lng,lat]
  rlog(`  Geoapify: POST ${GEOAPIFY_ROUTE_URL}`);
  let res;
  try {
    res = await fetch(GEOAPIFY_ROUTE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ points, avoid: avoidOptions }),
      signal,
    });
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    rwarn('  Geoapify: proxy unreachable (network error)', err.message);
    return null; // proxy unreachable → fall back
  }
  if (!res.ok) {
    rwarn(`  Geoapify: proxy returned ${res.status}`, res.status === 503 ? '(likely GEOAPIFY key unset)' : '');
    return null; // 503 (key unset), 502 (geoapify error), etc. → fall back
  }
  const data = await res.json().catch(() => null);
  if (!data?.geometry?.coordinates?.length) { rwarn('  Geoapify: empty/malformed response'); return null; }
  return { geometry: data.geometry, distance: data.distance, duration: data.duration, legs: data.legs || undefined };
}

async function fetchValhalla(host, valid, profile, avoidOptions, signal) {
  const costing = COSTING[profile] || 'auto';
  const body = {
    locations: valid.map(wp => {
      const loc = {
        lon: wp.lng,
        lat: wp.lat,
        // break_through forces Valhalla to enter and exit on DIFFERENT road edges,
        // preventing U-turns and out-and-back retracing on dead-end spurs.
        type: wp.isLoopVia ? 'break_through' : 'break',
      };
      // Perpendicular heading hint: if we know the radial bearing from the loop
      // origin to this via-point, instruct Valhalla to traverse it sideways
      // (perpendicular to the spoke) rather than going straight in and back out.
      if (wp.isLoopVia && wp.bearing != null) {
        loc.heading = Math.round((wp.bearing + 90) % 360);
        loc.heading_tolerance = 60;
      }
      return loc;
    }),
    costing,
    costing_options: { [costing]: buildCostingOptions(costing, avoidOptions) },
    units: 'km',
    directions_type: 'none',
  };

  rlog(`  Valhalla: POST ${host}/route`);
  const t0 = rnow();
  let res;
  try {
    res = await fetch(`${host}/route`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    rwarn(`  Valhalla (${host}): network error after ${Math.round(rnow() - t0)}ms`, err.message);
    throw new Error('network');
  }

  if (!res.ok) {
    let message = `Routing failed (${res.status})`;
    try { const e = await res.json(); if (e.error) message = e.error; } catch { /* ignore */ }
    rwarn(`  Valhalla (${host}): ${message} after ${Math.round(rnow() - t0)}ms`);
    throw new Error(message);
  }

  const data = await res.json();
  if (!data.trip?.legs?.length) { rwarn(`  Valhalla (${host}): no route in response`); throw new Error('No route found between these points.'); }
  rlog(`  Valhalla (${host}): responded in ${Math.round(rnow() - t0)}ms`);

  const coords = [];
  const legs = [];
  for (const leg of data.trip.legs) {
    const legCoords = decodePolyline6(leg.shape);
    coords.push(...(coords.length ? legCoords.slice(1) : legCoords));
    legs.push({
      geometry: { type: 'LineString', coordinates: legCoords },
      distance: Math.round(leg.summary.length * 1000),
      duration: Math.round(leg.summary.time),
    });
  }

  return {
    geometry: { type: 'LineString', coordinates: coords },
    distance: Math.round(data.trip.summary.length * 1000),
    duration: Math.round(data.trip.summary.time),
    // One entry per waypoint pair — lets a single moved waypoint re-route only
    // its adjacent legs instead of the whole route (see fetchStaleLegs below).
    legs,
  };
}

async function fetchOSRM(valid, profile, signal) {
  const osrmProfile = OSRM_PROFILE[profile] || 'driving';

  // OSRM treats every waypoint as a hard stop → loop vias cause hairpin turns.
  // Keep only: start, up to one mid via-point, end.  Shape stays guided but clean.
  const loopVias = valid.filter(wp => wp.isLoopVia);
  const nonVias  = valid.filter(wp => !wp.isLoopVia);
  let trimmed = nonVias;
  if (loopVias.length > 0) {
    const mid = loopVias[Math.floor(loopVias.length / 2)];
    trimmed = [...nonVias.slice(0, -1), mid, nonVias[nonVias.length - 1]];
  }

  const coords = trimmed.map(wp => `${wp.lng},${wp.lat}`).join(';');
  const url = `${OSRM_BASE}/route/v1/${osrmProfile}/${coords}?overview=full&geometries=geojson`;

  rlog(`  OSRM: GET ${url}`);
  const t0 = rnow();
  let res;
  try {
    res = await fetch(url, { signal });
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    rwarn(`  OSRM: network error after ${Math.round(rnow() - t0)}ms`, err.message);
    throw new Error('Could not reach any routing server — check your connection.');
  }

  if (!res.ok) {
    rwarn(`  OSRM: ${res.status} after ${Math.round(rnow() - t0)}ms`);
    throw new Error(`Routing failed (${res.status})`);
  }

  const data = await res.json();
  if (data.code !== 'Ok' || !data.routes?.length) {
    rwarn(`  OSRM: no route in response (code=${data.code})`);
    throw new Error('No route found between these points.');
  }
  rlog(`  OSRM: responded in ${Math.round(rnow() - t0)}ms`);

  const route = data.routes[0];
  return {
    geometry: route.geometry,
    distance: Math.round(route.distance),
    duration: Math.round(route.duration),
  };
}

// Valhalla polyline6: lat/lng order, divide by 1e6, output [lon, lat] for GeoJSON.
function decodePolyline6(encoded) {
  const coords = [];
  let index = 0, lat = 0, lng = 0;
  while (index < encoded.length) {
    let b, shift = 0, result = 0;
    do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lat += (result & 1) ? ~(result >> 1) : result >> 1;
    shift = 0; result = 0;
    do { b = encoded.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lng += (result & 1) ? ~(result >> 1) : result >> 1;
    coords.push([lng / 1e6, lat / 1e6]);
  }
  return coords;
}

function buildCostingOptions(costing, avoid = {}) {
  if (costing === 'auto') {
    return {
      // Default 0.25 = prefer secondary/scenic roads without completely
      // banning motorways (a motorcycle app shouldn't route via motorways
      // unless the user is just A→B and no other road exists).
      use_highways: avoid.motorway ? 0.0 : 0.25,
      use_tolls:    avoid.toll     ? 0.0 : 1.0,
      use_ferry:    avoid.ferry    ? 0.0 : 1.0,
      use_hills:    0.7,
      // Avoid unpaved tracks — they mostly lead to dead-end peak access roads
      // that cause retracing. Viewpoints on proper sealed roads are still used.
      use_tracks:   0.0,
    };
  }
  if (costing === 'bicycle' || costing === 'pedestrian') {
    return { use_ferry: avoid.ferry ? 0.0 : 1.0 };
  }
  return {};
}
